/**
 * Registration form component with validation
 */
import React, { useState } from 'react';
import { useAuth, RegisterData, AuthError } from '../../contexts/AuthContext';
import { orDefault } from './authCopy';
import {
  FmBanner,
  FmBox,
  FmButton,
  FmSeparator,
  FmStack,
  FmText,
  FmForm,
  useFmForm,
  useFmToast,
} from '@/components/ui';
import { useLanguage } from '@/i18n';
import type { FmFormSectionDescriptor } from '@/components/ui';

interface RegisterFormProps {
  onSuccess?: () => void;
  onSwitchToLogin?: () => void;
}

type AuthCopy = ReturnType<typeof useLanguage>['t']['auth'] | undefined;

/** Resolve all localized register-form copy with English fallbacks (kept out of
 * the component body so its JSX stays free of `||`-fallback branching). */
function resolveRegisterCopy(rawAuth: AuthCopy) {
  const auth: Partial<NonNullable<AuthCopy>> = rawAuth ?? {};
  const v: Partial<NonNullable<NonNullable<AuthCopy>['validation']>> = auth.validation ?? {};
  const sentryConsent = auth.sentryConsent;
  if (typeof sentryConsent !== 'string' || sentryConsent.trim().length === 0) {
    throw new Error('Missing registration Sentry consent translation.');
  }
  return {
    title: orDefault(auth.registerTitle, 'Create Account'),
    subtitle: orDefault(
      auth.registerSubtitle,
      'Join to access forest property data and management tools',
    ),
    username: orDefault(auth.username, 'Username'),
    usernamePlaceholder: orDefault(auth.usernamePlaceholder, 'Choose a username'),
    usernameRequired: orDefault(v.usernameRequired, 'Please enter a username'),
    usernameMin: orDefault(v.usernameMin, 'Username must be at least 3 characters'),
    usernamePattern: orDefault(
      v.usernamePattern,
      'Username can only contain letters, numbers, hyphens, and underscores',
    ),
    email: orDefault(auth.email, 'Email'),
    emailRegisterPlaceholder: orDefault(auth.emailRegisterPlaceholder, 'Enter your email address'),
    emailRequired: orDefault(v.emailRequired, 'Please enter your email'),
    emailInvalidFull: orDefault(v.emailInvalidFull, 'Please enter a valid email address'),
    firstName: orDefault(auth.firstName, 'First Name'),
    firstNamePlaceholder: orDefault(auth.firstNamePlaceholder, 'First name'),
    firstNameRequired: orDefault(v.firstNameRequired, 'Please enter your first name'),
    firstNameMin: orDefault(v.firstNameMin, 'First name must be at least 2 characters'),
    lastName: orDefault(auth.lastName, 'Last Name'),
    lastNamePlaceholder: orDefault(auth.lastNamePlaceholder, 'Last name'),
    lastNameRequired: orDefault(v.lastNameRequired, 'Please enter your last name'),
    lastNameMin: orDefault(v.lastNameMin, 'Last name must be at least 2 characters'),
    organizationLabel: orDefault(auth.organizationLabel, 'Organization (Optional)'),
    organizationHelp: orDefault(
      auth.organizationHelp,
      'Company, forestry association, or organization you represent',
    ),
    organizationPlaceholder: orDefault(
      auth.organizationPlaceholder,
      'e.g., Metsänomistajien liitto',
    ),
    password: orDefault(auth.password, 'Password'),
    passwordCreatePlaceholder: orDefault(auth.passwordCreatePlaceholder, 'Create a strong password'),
    registerPasswordRequired: orDefault(v.registerPasswordRequired, 'Please enter a password'),
    registerPasswordMin8: orDefault(
      v.registerPasswordMin8,
      'Password must be at least 8 characters',
    ),
    registerPasswordStrength: orDefault(
      v.registerPasswordStrength,
      'Password must contain at least one lowercase letter, one uppercase letter, and one number',
    ),
    confirmPassword: orDefault(auth.confirmPassword, 'Confirm Password'),
    confirmPasswordPlaceholder: orDefault(
      auth.confirmPasswordPlaceholder,
      'Confirm your password',
    ),
    confirmRequired: orDefault(v.confirmRequired, 'Please confirm your password'),
    passwordMismatch: orDefault(v.passwordMismatch, 'Passwords do not match'),
    sentryConsent,
    registerButton: orDefault(auth.registerButton, 'Create Account'),
    orDivider: orDefault(auth.orDivider, 'or'),
    hasAccountPrompt: orDefault(auth.hasAccountPrompt, 'Already have an account?'),
    switchToLogin: orDefault(auth.switchToLogin, 'Sign In'),
    termsNotice: orDefault(
      auth.termsNotice,
      'By creating an account, you agree to our terms of service and privacy policy. Your forest data will be securely stored and only accessible to you.',
    ),
  };
}

type RegisterCopy = ReturnType<typeof resolveRegisterCopy>;

/** Map a registration failure to a localized message (falls back to a generic one). */
function resolveRegisterError(err: unknown, auth: AuthCopy): string {
  const fallback = orDefault(auth?.registrationFailed, 'Registration failed');
  if (err instanceof AuthError) {
    const errors = auth?.errors as Partial<Record<string, string>> | undefined;
    const localized = errors?.[err.code];
    return orDefault(localized, fallback);
  }
  return fallback;
}

export const RegisterForm: React.FC<RegisterFormProps> = ({ onSuccess, onSwitchToLogin }) => {
  const toast = useFmToast();
  const { register, isLoading } = useAuth();
  const { t } = useLanguage();
  const [error, setError] = useState<string | null>(null);
  const [form] = useFmForm();

  const copy = resolveRegisterCopy(t?.auth);

  const handleRegister = async (values: Record<string, unknown>) => {
    try {
      setError(null);

      // Validate password confirmation
      const typedValues = values as unknown as RegisterData & { confirmPassword: string };
      if (typedValues.password !== typedValues.confirmPassword) {
        form.setFieldError('confirmPassword', [copy.passwordMismatch]);
        return;
      }

      // Remove confirmPassword from the data sent to API
      const { confirmPassword: _confirmPassword, ...registerData } = typedValues;

      // The backend creates the account UNVERIFIED, so do not imply the user is
      // logged in — surface the verification-required state.
      await register(registerData);
      toast.success(
        orDefault(
          t?.auth?.verificationPendingMessage,
          'Registration successful! Please check your email to verify your account before signing in.',
        ),
      );
      onSuccess?.();
    } catch (err) {
      setError(resolveRegisterError(err, t?.auth));
    }
  };

  const formSections = buildRegisterFormSections(copy);

  return (
    <FmStack gap="lg">
      {error && (
        <FmBox>
          <FmBanner tone="error" title={error} data-testid="register-error" />
        </FmBox>
      )}

      <FmForm
        form={form}
        initialValues={{ sentry_consent: false }}
        sections={formSections}
        onSubmit={handleRegister}
        submit={{
          label: copy.registerButton,
          disabled: isLoading,
          testId: 'register-submit',
        }}
        disabled={isLoading}
        density="comfortable"
        testId="register-form"
      />

      {onSwitchToLogin && (
        <>
          <FmText size="caption" tone="muted">{copy.orDivider}</FmText>
          <FmSeparator />
          <FmStack align="center" gap="xs">
            <FmText as="span" tone="subtle">{copy.hasAccountPrompt} </FmText>
            <FmButton variant="link" onClick={onSwitchToLogin}>
              {copy.switchToLogin}
            </FmButton>
          </FmStack>
        </>
      )}

      <FmBox inset="md">
        <FmText as="span" tone="subtle" size="fine">
          {copy.termsNotice}
        </FmText>
      </FmBox>
    </FmStack>
  );
};

const buildRegisterFormSections = (copy: RegisterCopy): FmFormSectionDescriptor[] => [
    {
      title: copy.title,
      description: copy.subtitle,
      fieldDescriptors: [
        {
          kind: 'text',
          name: 'username',
          label: copy.username,
          placeholder: copy.usernamePlaceholder,
          required: true,
          validation: {
            rules: [
              { min: 3, message: copy.usernameMin },
              { pattern: /^[a-zA-Z0-9_-]+$/, message: copy.usernamePattern },
            ],
            required: true,
            message: copy.usernameRequired,
          },
          testId: 'register-username',
        },
        {
          kind: 'text',
          name: 'email',
          label: copy.email,
          placeholder: copy.emailRegisterPlaceholder,
          type: 'email',
          required: true,
          validation: {
            rules: [
              { type: 'email', message: copy.emailInvalidFull },
            ],
            required: true,
            message: copy.emailRequired,
          },
          testId: 'register-email',
        },
        {
          kind: 'text',
          name: 'first_name',
          label: copy.firstName,
          placeholder: copy.firstNamePlaceholder,
          required: true,
          validation: {
            rules: [
              { min: 2, message: copy.firstNameMin },
            ],
            required: true,
            message: copy.firstNameRequired,
          },
          testId: 'register-first-name',
        },
        {
          kind: 'text',
          name: 'last_name',
          label: copy.lastName,
          placeholder: copy.lastNamePlaceholder,
          required: true,
          validation: {
            rules: [
              { min: 2, message: copy.lastNameMin },
            ],
            required: true,
            message: copy.lastNameRequired,
          },
          testId: 'register-last-name',
        },
        {
          kind: 'text',
          name: 'organization',
          label: copy.organizationLabel,
          placeholder: copy.organizationPlaceholder,
          helper: copy.organizationHelp,
          required: false,
          testId: 'register-organization',
        },
        {
          kind: 'text',
          name: 'password',
          label: copy.password,
          placeholder: copy.passwordCreatePlaceholder,
          type: 'password',
          required: true,
          validation: {
            rules: [
              { min: 8, message: copy.registerPasswordMin8 },
              { pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/, message: copy.registerPasswordStrength },
            ],
            required: true,
            message: copy.registerPasswordRequired,
          },
          testId: 'register-password',
        },
        {
          kind: 'text',
          name: 'confirmPassword',
          label: copy.confirmPassword,
          placeholder: copy.confirmPasswordPlaceholder,
          type: 'password',
          required: true,
          validation: {
            required: true,
            message: copy.confirmRequired,
          },
          testId: 'register-confirm-password',
        },
        {
          kind: 'binary',
          control: 'checkbox',
          name: 'sentry_consent',
          label: 'Forest Sentry',
          text: copy.sentryConsent,
          testId: 'register-sentry-consent',
        },
      ],
    },
];
